import React, { useContext } from 'react'
import { AsYouType } from 'libphonenumber-js'
import { Typography, TextField as FoundationsTextField, Icon } from 'foundations-library/components'
import { createStyles, makeStyles } from '@material-ui/core'
import LeadContext from '../../LeadContext'
import { PasswordManagementForm } from 'password-and-session-management'

const useStyles = makeStyles(theme =>
    createStyles({
        requiredFieldsText: {
            color: `${theme.palette.info.dark} !important`,
            marginBottom: '15px'
        },
        footerText: {
            color: `${theme.palette.text.secondary} !important`,
            marginTop: '10px'
        },
        select: {
            height: '44px'
        },
        lockIcon: {
            width: '18px',
            height: '18px',
            marginBottom: '4px'
        },
        zipHelperText: {
            marginTop: '7px'
        },
        passwordManagementForm: {
            marginBottom: '20px'
        },
        header: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            alignSelf: 'stretch'
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '20px',
            alignSelf: 'stretch'
        },
        grouped: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '20px',
            alignSelf: 'stretch'
        },
        foundationsTextField: {
            '& .text-field': {
                border: 0
            }
        },
        fieldContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '8px',
            alignSelf: 'stretch'
        },
        helperText: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            alignSelf: 'stretch'
        },
        businessInfoForm: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '15px',
            flex: '1 0 0',
            '& .text-field': {
                border: 0
            }
        }
    })
)

const fieldLabels = {
    company_name: 'Company name', // eslint-disable-line @typescript-eslint/naming-convention
    first_name: 'First name', // eslint-disable-line @typescript-eslint/naming-convention
    last_name: 'Last name', // eslint-disable-line @typescript-eslint/naming-convention
    personal_phone_number: 'Mobile number', // eslint-disable-line @typescript-eslint/naming-convention
    password: 'Password',
    email: 'Email address'
}

/**
 * Renders inputs/fields collecting information about the user and their company.
 */
const BusinessInfoForm = (): JSX.Element => {
    const classes = useStyles()

    const { leadParams, setLeadParams, savingLead } = useContext(LeadContext)

    const disabled = savingLead

    const renderFoundationsField = (
        field: string,
        fieldType: string,
        onChange?: (value: string) => void | undefined,
        { wrapper, helperText }: { wrapper?: boolean, helperText?: string } = { wrapper: false, helperText: '' }
    ) : JSX.Element => {
        const onFieldChange = (value: string) => {
            if (onChange) {
                onChange(value)
            } else {
                setLeadParams({ ...leadParams, [field]: value })
            }
        }

        const getAutoCompleteAttribute = () => {
            switch (field) {
                    case 'first_name':
                        return 'given-name'
                    case 'last_name':
                        return 'family-name'
                    case 'company_name':
                        return 'organization'
                    case 'email':
                        return 'email'
                    case 'personal_phone_number':
                        return 'tel'
                    default:
                        return undefined
            }
        }

        const autoCompleteAttribute = getAutoCompleteAttribute()

        // console.log('renderFoundationsField', field, fieldType, fieldLabels[field], leadParams[field] || '', onFieldChange, disabled)
        const foundationsField = (
            <FoundationsTextField
                data-testid={field}
                name={field}
                type={fieldType}
                label={fieldLabels[field as keyof typeof fieldLabels]}
                value={leadParams[field as keyof typeof leadParams] as string || ''}
                onChange={onFieldChange}
                disabled={disabled}
                fullWidth
                className={classes.foundationsTextField}
                {...(autoCompleteAttribute ? { autoComplete: autoCompleteAttribute } : {})}
            />
        )

        return (
            wrapper
                ? <div className={classes.fieldContainer}>
                    {foundationsField}
                    <div className={classes.helperText}>
                        <Icon name='notification-alert' size='small' color='neutral-400' />
                        <Typography variant="body4" color="neutral-400">{helperText}</Typography>
                    </div>
                </div>
                : foundationsField
        )
    }
    console.log('leadParams', leadParams)
    return (
        <div className={classes.businessInfoForm}>
            <div className={classes.header}>
                <Typography variant='h7' color='neutral-900'>
                    Business information
                </Typography>

                <Typography variant="body4" color="neutral-900">
                    All fields are required
                </Typography>
            </div>

            <div className={classes.form}>
                <div className={classes.grouped}>
                    {renderFoundationsField('first_name', 'text')}
                    {renderFoundationsField('last_name', 'text')}
                </div>
                {renderFoundationsField('company_name', 'text')}
                {renderFoundationsField('email', 'email')}
                {renderFoundationsField('personal_phone_number', 'tel', value => {
                    // Format the number as the user types, except when the user is removing/backspacing characters (prevents bugs/glitches due to formatting re-introducing things like brackets)
                    const inputValue = value.slice(0, 16)
                    const formattedNumber = inputValue.length >= (leadParams.personal_phone_number?.length || 0) ? new AsYouType('US').input(inputValue) : inputValue
                    setLeadParams({ ...leadParams, personal_phone_number: formattedNumber.slice(0, 16) }) // eslint-disable-line @typescript-eslint/naming-convention
                }, { wrapper: true, helperText: 'Used for verification purposes' })}
                <PasswordManagementForm
                    mode='create'
                    onChangeValues={(val) => setLeadParams({ ...leadParams, password: val.new, confirmPassword: val.confirm })}
                    theme='foundations'
                    className={classes.passwordManagementForm}
                    disabled={disabled}
                />
            </div>
        </div>
    )
}

export default BusinessInfoForm
